import { uniqBy } from 'lodash';

export interface Connection<TEdge> {
  totalCount?: number;
  pageInfo?: { hasNextPage?: boolean };
  edges?: TEdge[];
}

/**
 * 첫 페이지 이후 남은 목록을 한 번의 요청으로 다시 받아 전체 목록을 반환한다.
 *
 * 서버 정렬은 정렬값(예: 시작시각)이 같은 항목끼리 순서가 요청마다 달라질 수 있어서,
 * 페이지를 나눠 받으면 경계에 걸친 항목이 중복되거나 빠진다.
 * (예약 캘린더: 같은 시각 10건 중 3건 중복·3건 누락 → 다른 상품 일정이 통째로 사라짐)
 * 그래서 다음 페이지가 있으면 totalCount 만큼 한 번에 다시 받는다.
 * 서버가 size 를 제한했거나 그 사이 항목이 늘어 한 번에 다 못 받은 경우에만,
 * 실제로 받은 개수를 size 로 이어 받고 id 로 중복을 제거한다.
 *
 * @param firstPage - 이미 받은 첫 페이지
 * @param isStale - true 가 되면 이어 받기를 멈춘다 (호출부에서 결과를 버릴 때)
 * @param fetchPage - page/size 로 한 페이지를 받아오는 함수
 */
export async function fetchAllAtOnce<TEdge extends { node: { id: string } }>(
  firstPage: Connection<TEdge> | undefined,
  isStale: () => boolean,
  fetchPage: (
    page: number,
    size: number,
  ) => Promise<Connection<TEdge> | undefined>,
): Promise<TEdge[]> {
  if (!firstPage?.pageInfo?.hasNextPage) {
    return firstPage?.edges || [];
  }

  const all = await fetchPage(
    1,
    Math.max(firstPage.totalCount || 0, firstPage.edges?.length || 0),
  );
  let edges = all?.edges || [];
  let hasMore = all?.pageInfo?.hasNextPage || false;

  const size = edges.length;
  let page = 1;

  while (hasMore && size > 0 && !isStale()) {
    page++;
    const next = await fetchPage(page, size);
    edges = [...edges, ...(next?.edges || [])];
    hasMore = next?.pageInfo?.hasNextPage || false;
  }

  return uniqBy(edges, edge => edge.node.id);
}
